import { Link } from '@heidi/design-system/link';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Gift } from 'lucide-react';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { formatCreditAllowanceCycleEndDate } from '../credit-usage-popover/format-credit-allowance-reset-label';
import { complimentaryTrialEndOfferMessages as messages } from './complimentary-trial-end.messages';

/** Discount on a yearly Max seat; the price itself is applied in Stripe. */
export const COMPLIMENTARY_TRIAL_END_YEARLY_DISCOUNT_PERCENT = 15;

export type ComplimentaryTrialEndOfferCardProps = {
  /** When complimentary usage ends, which is also when the offer ends. */
  cycleEnd: string;
  conditionsHref: string;
  className?: string;
};

/** The yearly Max seat offer shown in the T-7 modal and the upgrade checkout. */
export function ComplimentaryTrialEndOfferCard({
  cycleEnd,
  conditionsHref,
  className,
}: ComplimentaryTrialEndOfferCardProps) {
  const intl = useIntl();

  return (
    <div
      className={cn(
        'flex w-full items-start gap-2.5 rounded-xl border border-border bg-surface-accent p-4 text-start text-text-primary',
        className
      )}
      data-testid="complimentary-trial-end-offer"
    >
      <span className="flex pt-0.5">
        <Gift className="size-[18px] shrink-0" aria-hidden />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <TypographyP2Bold>
          <FormattedMessage
            {...messages.title}
            values={{ percent: COMPLIMENTARY_TRIAL_END_YEARLY_DISCOUNT_PERCENT }}
          />
        </TypographyP2Bold>
        <TypographyCaption>
          <FormattedMessage
            {...messages.caption}
            values={{
              date: formatCreditAllowanceCycleEndDate(intl, cycleEnd),
              conditions: (chunks: ReactNode) => (
                <Link
                  variant="inline"
                  size="inherit"
                  className="text-text-primary decoration-text-primary hover:text-text-primary hover:decoration-text-primary focus-visible:text-text-primary"
                  href={conditionsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {chunks}
                </Link>
              ),
            }}
          />
        </TypographyCaption>
      </div>
    </div>
  );
}
